import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                    from '../../api/api.service';
import { CodeTypeModel }                 from '../../core/models/code-type';
import { CustomerModel }                 from '../../customers/models/customer';
import { DateParserService }             from '../../core/services/date-parser.service';
import { JobBulkPrintModel }             from './typings/transaction-center.jobs.bulk-print';
import { JobStartAndPrintComponent }     from './transaction-center.jobs.start-and-print.component';
import { JobStartAndPrintTypeModel }     from './typings/transaction-center.jobs.start-and-print-type';
import { MessagesService }               from '../../messages/messages.service';
import { TableQueryParameters }          from '../../table/types/table-query-parameters';
import { TransactionCenterFiltersModel } from '../typings/transaction-center.filters';
import { TransactionCenterJobModel }     from './typings/transaction-center.job';

@Injectable({
  providedIn: 'root'
})
export class JobsService {
  public broadcastKey  = 'filterTransactionCenterJobs';
  public tableCacheKey = 'transactionCenterJobs';

  constructor (
    private apiService        : ApiService,
    private dateParserService : DateParserService,
    private messagesService   : MessagesService,
    private modalService      : NgbModal,
    private translateService  : TranslateService
  ) {}

  bulkPrint ( bulkPrint : JobBulkPrintModel ) : Promise<any> {
    return this.apiService.post('/reports/jasper_job_card_bulk', bulkPrint, 'print', null, {
        responseType: 'arraybuffer'
      });
  }

  getEventsForDate ( date : string ) : Promise<any> {
    return this.apiService.get('/events/get_events_for_today', date);
  }

  getBulkSmsCustomers ( filters : any ) : Promise<Array<CustomerModel>> {
    return this.apiService.get('/customers/customer_list_for_bulk_sms', [
      filters.startDate   || '*',
      filters.endDate     || '*',
      filters.invoiceType || '*',
      filters.jobStatus   || '*'
    ]);
  }

  getInvoicesForDate ( date : string ) : Promise<any> {
    return this.apiService.get('/invoices/get_jobs_for_today', date);
  }

  getConstraints () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/job_filter_constraint_types');
  }

  get ( filters : TransactionCenterFiltersModel = {}, params  : TableQueryParameters = {} ) : Promise<Array<TransactionCenterJobModel>> {
    return this.apiService.stream('/job_center', [
        filters.value       || '*',
        params.offset       || 0,
        params.pageSize     || 10,
        filters.postDate    || '*',
        filters.constrainBy || '*',
        filters.jobStatus   || '*',
        ( params.field || 'invoice.post_date' ).replace('job.', ''),
        params.direction || 'desc'
      ]);
  }

  getCount ( filters : TransactionCenterFiltersModel = {} ) : Promise<number> {
    return this.apiService.get('/job_center/count', [
        filters.value       || '*',
        filters.postDate    || '*',
        filters.constrainBy || '*',
        filters.jobStatus   || '*'
      ]);
  }

  getStatuses () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/job_status_types');
  }

  getStatusOptions () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/filter_job_status_types');
  }

  printForDate () : Promise<any> {
    return this.startAndPrint('printOnly');
  }

  startForDate () : Promise<any> {
    return this.startAndPrint('startOnly');
  }

  startAndPrintForDate () : Promise<any> {
    return this.startAndPrint('startAndPrint');
  }

  startAndPrint ( type : JobStartAndPrintTypeModel ) : Promise<any> {
    const modal = this.modalService.open(JobStartAndPrintComponent);

    modal.componentInstance.type = type;

    return modal.result;
  }

  // every open booking on the day becomes a job card; the toast is sentence-cased, so the day is written in numbers
  startJobsForDate ( date : string ) : Promise<void> {
    return this.getEventsForDate(date)
      .then(() => {
        this.messagesService.show(this.translateService.instant('BOOKING_SPACE.INVOICES_CREATED_FOR_DATE', {
          date: this.dateParserService.format(date, this.dateParserService.getFormat(true).toUpperCase())
        }), 'success');
      });
  }
}
